<template>
    <div class="card" v-if="loaded">

        <div class="card-header border-0">
            <div class="row">
                <div class="col-4">
                    <h3 class="mb-0 text-uppercase">
                        {{ $t('common.sessions') }}
                    </h3>
                </div>
                <div class="col-8 text-right">

                    <base-button type="secondary" size="sm" @click="showResults = !showResults">
                        {{ showResults ? $t('common.hide-results') : $t('common.show-results') }}
                    </base-button>
            
                    <base-button type="secondary" size="sm" @click="clearFilters" v-if="hasFilters">
                        {{ $t('common.clear-filters') }}
                    </base-button>

                    <base-button type="primary" size="sm" @click="openFilters()">
                        {{ $t('common.more-filters') }}
                    </base-button>
                </div>
            </div>
        </div>

        <div class="pt-2">

            <table-control :paging="ctrl.paging" :search="true" v-model="search" ref="search"></table-control>

            <el-table class="table-responsive table-flush"
                    header-row-class-name="thead-light"
                    :data="ctrl.rows"
                    @sort-change="sortChange"
                    @filter-change="tableFiltersChange"
                    ref="table">

                <el-table-column class-name="avatars" width="80">
                    <template v-slot="{row}">
                        <span :class="'float-left status-bullet text-'+sessionColor(row)+' ml-1 mt-3'" v-if="offlineSession(row.id)">
                            <i class="fas fa-circle"></i>
                        </span>
                        <div class="pl-3">
                            <a href="#" class="avatar rounded-circle" @click.prevent="filterByLearner(row.learner)"
                                v-if="sessionFeatures.canUseAllFilters() && !learnerId"
                            >
                                <img :src="row.learner.avatar">
                            </a>
                            <span class="avatar rounded-circle" v-else>
                                <img :src="row.learner.avatar">
                            </span>
                        </div>
                    </template>
                </el-table-column>

                <el-table-column class-name="learners" :label="$t('common.learner')">
                    <template v-slot="{row}">
                        <h4 class="mb-0">
                            <a href="#" @click.prevent="filterByLearner(row.learner)"
                                v-if="sessionFeatures.canUseAllFilters() && !learnerId"
                            >
                                {{row.learner.name}}
                            </a>
                            <span v-else>
                                {{row.learner.name}}
                            </span>
                        </h4>
                        <small v-if="row.learner_owner_name">
                            {{ row.learner_owner_name }}
                        </small>
                        <small v-if="row.learner_entity_name">
                            / {{ row.learner_entity_name }}
                        </small>
                    </template>
                </el-table-column>

                <el-table-column :label="$t('common.purpose')">
                    <template v-slot="{row}">
                        <h4 class="mb-0">
                            <small>{{ $t('common.cohort') }}:</small>
                            <a href="#" @click.prevent="filterByCohort(row.cohort)"
                                v-if="sessionFeatures.canUseAllFilters()"
                            >
                                {{ row.cohort.name }}
                            </a>
                            <span v-else>
                                {{ row.cohort.name }}
                            </span>
                        </h4>
                        <h4 class="mb-0">
                            <small>{{ $t('common.degree') }}:</small>
                            <a href="#" @click.prevent="filterByDegree(row.degree)">
                                {{ row.degree.name }}
                            </a>
                        </h4>
                        <h4 class="mb-0" v-if="row.simulation">
                            <small>{{ $t('common.sim') }}:</small>
                            {{ simulationName(row) }}
                        </h4>
                    </template>
                </el-table-column>

                <el-table-column :label="$t('common.status')" width="100"
                    column-key="status"
                    :filters="filtering.unsortedFilteringValues(statusData)"
                    :filtered-value="filters.status"
                    filter-placement="bottom-end"
                >
                    <template v-slot="{row}">
                        <badge :type="statusColor(row.status)">{{ statusLabel(row.status) }}</badge>
                    </template>
                </el-table-column>

                <el-table-column :label="$t('common.session')"
                    sortable="custom" prop="scheduled_at"
                >
                    <template v-slot="{row}">
                        <div v-if="row.scheduled_at">
                            <i class="fas fa-calendar-alt mr-1"></i> {{ scheduledDate(row) }}
                        </div>
                        <div v-if="row.tester1">
                            <a href="#" @click.prevent="filterByTester(row.tester1)" class="black-link"
                                v-if="sessionFeatures.canUseAllFilters()"
                            >
                                <i class="fas fa-pencil-alt mr-1"></i> {{ row.tester1.name }}
                            </a>
                            <span v-else>
                                <i class="fas fa-pencil-alt mr-1"></i> {{ row.tester1.name }}
                            </span>
                        </div>
                        <div v-if="row.tester2">
                            <a href="#" @click.prevent="filterByTester(row.tester2)" class="black-link"
                                v-if="sessionFeatures.canUseAllFilters()"
                            >
                                <i class="fas fa-pencil-alt mr-1"></i> {{ row.tester2.name }}
                            </a>
                            <span v-else>
                                <i class="fas fa-pencil-alt mr-1"></i> {{ row.tester2.name }}
                            </span>
                        </div>
                    </template>
                </el-table-column>

                <el-table-column class-name="mode" :label="$t('common.mode')"
                    column-key="mode"
                    :filters="filtering.unsortedFilteringValues(modeData)"
                    :filtered-value="filters.mode"
                    filter-placement="bottom-end"
                    width="100"
                    align="center"
                >
                    <template v-slot="{row}">

                        <div v-if="row.mode == 'resit'">
                            <i class="fas fa-undo mr-1"></i>
                        </div>

                    </template>
                </el-table-column>

                <el-table-column class-name="result" :label="$t('common.result')"
                    column-key="gonogo"
                    :filters="filtering.filteringValues(gonogoSelect)"
                    :filtered-value="filters.gonogo"
                    filter-placement="bottom-end"
                    width="100"
                    align="center"
                    v-if="showResults"
                >
                    <template v-slot="{row}">

                        <badge size="lg" :type="gonogoColor(row.gonogo)" v-if="resultAvailable(row)">
                            {{ gonogoLabel(row.gonogo) }}
                        </badge>

                    </template>
                </el-table-column>

                <el-table-column align="right" width="100">
                    <template v-slot="{row}">
                        <div class="p-1">

                            <base-button type="primary" size="sm" @click="sessionFeatures.openSession(row)" v-if="sessionFeatures.canOpen(row)">
                                {{ $t('dashboard.more') }}
                            </base-button>

                            <base-button type="secondary" size="sm" @click="sessionFeatures.openSession(row)" v-if="sessionFeatures.canEdit(row)">
                                <i class="fas fa-pencil-alt mr-1"></i> {{ $t('common.edit') }}
                            </base-button>
                        </div>
                        
                    </template>
                </el-table-column>

                                                
                <el-table-column align="right" width="50" class-name="actions">
                    <template v-slot="{row}">
                        <div class="table-actions">
                            <actions-dropdown :actions="actions(row)"></actions-dropdown>
                        </div>
                    </template>
                </el-table-column>

            </el-table>

            <table-pagination :paging="ctrl.paging"></table-pagination>
        </div>

        <portal to="modals">

            <form-modal v-model="ctrl.modals.filters"
                :title="$t('common.filters')"
            >
                <session-filters
                    :modal="ctrl.modals.filters"
                    :value="filters"
                    @input="globalFiltersChange"
                    :learner-id="learnerId"
                    :features="sessionFeatures"
                >
                </session-filters>

            </form-modal>

            <confirm-modal v-model="ctrl.modals.confirmCreate">
                <p>{{ $t('sessions.create-confirm') }}</p>
            </confirm-modal>

            <confirm-modal v-model="ctrl.modals.confirmDelete">
                <p>{{ $t('sessions.delete-confirm') }}</p>
            </confirm-modal>

            <confirm-modal v-model="ctrl.modals.confirmReset">
                <p>{{ $t('sessions.reset-confirm') }}</p>
            </confirm-modal>

            <!-- Offline modals -->

            <confirm-modal v-model="ctrl.modals.offlineConfirmRemove">
                <p>{{ $t('pwa.remove-confirm') }}</p>
            </confirm-modal>

            <sync-modal v-model="ctrl.modals.offlineSync" :sync="sync" :autoRemoveAfterSync="true" @done="syncDone">
            </sync-modal>

            <base-modal
                v-model="ctrl.modals.offlineError"
                size="sm"
                modal-classes="modal-danger"
                modal-content-classes="bg-gradient-danger"
            >
                <div class="py-3 text-center">
                    <i class="tim-icons icon-t-warning icon-3x"></i>
                    <h4 class="heading mt-4">{{ offlineErrorMessage }}</h4>
                </div>
            </base-modal>

            <base-modal
                v-model="ctrl.modals.offlineConflict"
                modal-classes="modal-danger"
                modal-content-classes="bg-gradient-danger"
            >
                <div class="py-3 text-center">
                    <i class="tim-icons icon-t-warning icon-3x"></i>
                    <h4 class="heading mt-4">{{ $t('common.confirm-question') }}</h4>
                    <p>{{ $t('pwa.http-error-409') }}</p>
                </div>

                <template slot="footer">
                    <div class="text-center">
                        <base-button type="white" @click="confirmDownload">{{ $t('common.confirm') }}</base-button>
                        <base-button type="link" class="text-white" @click="cancelDownload">{{ $t('common.cancel') }}</base-button>
                    </div>
                </template>
            </base-modal>

        </portal>
    </div>
</template>

<script>
    import moment from 'moment'
    import CrudController from '@trax/classes/CrudController'
    import CrudFiltering from '../../../classes/CrudFiltering'
    import SessionFilters from './SessionFilters'
    import SessionFeatures from './SessionFeatures'
    import GradeFeatures from '../grades/GradeFeatures'
    import SyncModal from '../offline/SyncModal'

    export default {

        components: {
            SessionFilters, SyncModal
        },

        data() {
            let sessionFeatures = new SessionFeatures(this.$router, this.$auth)
            let gradeFeatures = new GradeFeatures(this.$router, this.$auth)
            let filtering = new CrudFiltering('dashboard_sessions')
            let sortDefault = ['-registration_id', 'learner.lastname']
            return {
                loaded: false,
                sessionFeatures,
                gradeFeatures,
                filtering,
                sortDefault,
                ctrl: new CrudController({
                    endpoint: '/socrates/api/front/dashboard/store/sessions',
                    mapData: sessionFeatures.mapData,
                    unmapData: sessionFeatures.unmapData,
                    modals: ['filters', 'confirmCreate', 'confirmReset', 'offlineError', 'offlineConflict', 'offlineConfirmRemove', 'offlineSync'],
                    paging: {
                        perPage: 10,
                        perPageOptions: [10, 25, 50],
                        total: 0
                    },
                    query: {
                        sort: sortDefault,
                        relations: ['learner', 'tester1', 'tester2', 'registration', 'cohort', 'degree', 'simulation'],
                        include: ['status_colors', 'status_data', 'gonogo_select', 'gonogo_colors', 'mode_data'],
                        accessors: ['learner_owner_name', 'learner_entity_name'],
                    },
                    callbacks: {
                        listed: this.listed,
                    }
                }),
                statusData: [],
                statusColors: {},
                gonogoSelect: {},
                gonogoColors: {},
                modeData: {},
                moment: moment,
                search: '',
                showResults: false,
                filters: {},

                // Offline.
                offlineCurrentId: null,
                offlineSessions: [],
                offlineErrorMessage: null,
                sync: {
                    endpoint: '/socrates/api/front/offline/sessions',
                    params: {},
                }
            }
        },

        created() {

            this.filtering.init(this)

            this.ctrl.query.filters = this.getFilters()

            this.ctrl.init(this, 'ctrl').then(resp => {
                this.loaded = true
                this.statusData = resp.data.included.status_data
                this.statusColors = resp.data.included.status_colors
                this.gonogoSelect = resp.data.included.gonogo_select
                this.gonogoColors = resp.data.included.gonogo_colors
                this.modeData = resp.data.included.mode_data
                this.loadOfflineStatus()
            })
        },

        mounted() {
            if (this.filters.simpleSearch) {
                this.$refs.search.setFilter(this.filters.simpleSearch)
            }
        },

        watch: {
            search(val) {
                if (val === '') {
                    delete this.filters.simpleSearch
                } else {
                    this.filters.simpleSearch = val
                }
                this.filters = {...this.filters}    // Clone to force reactivity
                this.reload()
            }
        },

        computed: {

            empty() {
                return this.ctrl.rows.length == 0 && !this.search
            },

            hasFilters() {
                // First, remove null filters.
                Object.keys(this.filters).forEach(key => {
                    if (!this.filters[key]) {
                        delete this.filters[key]
                    }
                })

                if (this.registrationId && this.learnerId) {
                    return Object.keys(this.filters).length > 2 || this.ctrl.query.sort.toString() != this.sortDefault.toString()
                } else if (this.registrationId) {
                    return Object.keys(this.filters).length > 1 || this.ctrl.query.sort.toString() != this.sortDefault.toString()
                } else {
                    return Object.keys(this.filters).length > 0 || this.ctrl.query.sort.toString() != this.sortDefault.toString()
                }
            },

            registrationId() {
                return this.$route.params.registrationId
            },

            learnerId() {
                return this.$route.params.learnerId
            },
        },

        methods: {

            // -------------------- Offline methods.

            offlineSession(sessionId) {
                return this.offlineSessions.find(candidate => candidate.session.id == sessionId)
            },

            sessionColor(session) {
                return this.offlineSession(session.id).sync.color
            },
            
            sessionResyncable(session) {
                const sync = this.offlineSession(session.id).sync
                return sync.color == 'success' && (
                    sync.evaluations.synced
                    || sync.feedbacks.synced
                    || sync.issues.synced
                    || sync.observations.synced
                )
            },
            
            sessionDownloadable(session) {
                return (session.tester1_id == this.$auth.user.id || session.tester2_id == this.$auth.user.id)
                    && (session.status == 'started' || session.status == 'completed');
            },

            downloadSession(id, force = true) {
                this.offlineCurrentId = id
                const session = this.ctrl.rows.find(session => session.id == id)

                this.$loader.show()
                axios.post(this.sync.endpoint, {
                    session_id: id,
                    registration_id: session.registration_id,
                    simulation_id: session.simulation_id,
                    force: force
                })
                .then(resp => {
                    this.$loader.hide()
                    this.refreshOfflineSessions(resp.data.data)
                }).catch(error => {
                    this.$loader.hide()
                    this.handleOfflineError(error)
                })
            },

            confirmDownload() {
                this.ctrl.modals.offlineConflict.show = false
                this.downloadSession(this.offlineCurrentId, true)
            },

            cancelDownload() {
                this.ctrl.modals.offlineConflict.show = false
            },

            syncSession(id, force = false) {
                const session = this.ctrl.rows.find(session => session.id == id)
                this.sync.params.session_id = id
                this.sync.params.registration_id = session.registration_id
                this.sync.params.simulation_id = session.simulation_id
                this.sync.params.force = force
                this.ctrl.modals.open('offlineSync')
            },

            // Currently unsued because local data is removed automatically after each sync.
            /*
            resyncSession(id) {
                this.syncSession(id, true)
            },
            */

            syncDone() {
                this.removeOfflineSessionNow(this.sync.params.session_id)
                /*
                this.ctrl.list().then(resp => {
                    this.loadOfflineStatus()
                })
                */
            },


            removeOfflineSession(id) {
                this.ctrl.modals.open('offlineConfirmRemove').then(confirmResponse => {
                    if (confirmResponse.confirmed) {
                        this.removeOfflineSessionNow(id)
                    }
                })
            },

            removeOfflineSessionNow(id) {
                const session = this.ctrl.rows.find(session => session.id == id)
                axios.delete(this.sync.endpoint, { data: {
                    session_id: id,
                    registration_id: session.registration_id,
                    simulation_id: session.simulation_id,
                }})
                .then(resp => {
                    this.refreshOfflineSessions(resp.data.data)
                }).catch(this.handleOfflineError)
            },

            loadOfflineStatus() {
                axios.get(this.sync.endpoint).then(resp => {
                    this.refreshOfflineSessions(resp.data.data)
                })
            },

            refreshOfflineSessions(sessions) {
                this.offlineSessions = sessions
                this.$emit('refresh-offline-status')
            },

            handleOfflineError(error) {
                if (!error.response) {
                    return
                }
                switch(error.response.status) {
                    case 409:
                        this.ctrl.modals.open('offlineConflict')
                        break
                    case 403:
                    case 404:
                    case 405:
                    case 444:
                        this.offlineErrorMessage = this.$t('pwa.http-error-' + error.response.status)
                        this.ctrl.modals.open('offlineError')
                        break
                    default:
                        this.$auth.handleErrorVm(error, this)
                }
            },

            // ---------------------

            simulationName(session) {
                let role = session.simulation.use_simulation_role2 && session.degree.simulation_role_2
                    ? session.degree.simulation_role_2_name[session.degree.lang]
                    : session.degree.simulation_role_1_name[session.degree.lang]

                return session.simulation.name + ' (' + role + ')'
            },

            actions(session) {
                let actions = []
                if (this.offlineSession(session.id)) {
                    if (this.sessionColor(session) == 'danger') {
                        actions.push({
                            label: this.$t('pwa.sync'),
                            handler: this.syncSession,
                            data: session.id
                        })
                    }
                    actions.push({
                        label: this.$t('pwa.remove'),
                        handler: this.removeOfflineSession,
                        data: session.id
                    })
                    /*
                    if (this.sessionResyncable(session)) {
                        actions.push({
                            label: this.$t('pwa.resync'),
                            handler: this.resyncSession,
                            data: session.id
                        })
                    }
                    */
                } else {
                    if (this.sessionFeatures.canCreate(session)) {
                        actions.push({
                            label: this.$t('sessions.create'),
                            handler: this.create,
                            data: session
                        })
                    }
                    if (this.sessionFeatures.canSwitchMode(session)) {
                        if (session.mode == 'resit') {
                            actions.push({
                                label: this.$t('sessions.switch-to-normal-mode'),
                                handler: this.switchToNormal,
                                data: session
                            })
                        } else {
                            actions.push({
                                label: this.$t('sessions.switch-to-resit-mode'),
                                handler: this.switchToResit,
                                data: session
                            })
                        }
                    }
                    if (this.sessionFeatures.canReset(session)) {
                        actions.push({
                            label: this.$t('sessions.reset-session'),
                            handler: this.resetSession,
                            data: session
                        })
                    }
                    if (this.sessionFeatures.canDelete(session)) {
                        actions.push({
                            label: this.$t('sessions.delete'),
                            handler: this.delete,
                            data: session
                        })
                    }
                    if (this.sessionDownloadable(session)) {
                        actions.push({
                            label: this.$t('pwa.download'),
                            handler: this.downloadSession,
                            data: session.id,
                            divided: true
                        })
                    }
                }
                return actions
            },

            resetSession(session) {
                this.ctrl.modals.open('confirmReset').then(resp => {
                    if (resp.confirmed) {
                        axios.post('/socrates/api/front/dashboard/data/sessions/'+session.id+'/reset')
                            .then(resp => {
                                this.reload()
                            })
                            .catch(err => {
                                this.$auth.handleErrorVm(err, this)
                            })        
                    }
                })
            },

            create(session) {
                this.ctrl.modals.open('confirmCreate').then(confirmResponse => {
                    if (confirmResponse.confirmed) {
                        axios.post('/socrates/api/front/dashboard/data/sessions/'+session.id+'/create')
                            .then(resp => {
                                this.reload()
                            })
                            .catch(err => {
                                this.$auth.handleErrorVm(err, this)
                            })        
                    }
                })
            },

            switchToNormal(session) {
                axios.post('/socrates/api/front/dashboard/data/sessions/'+session.id+'/mode/normal')
                    .then(resp => {
                        this.reload()
                    })
                    .catch(err => {
                        this.$auth.handleErrorVm(err, this)
                    })        
            },

            switchToResit(session) {
                axios.post('/socrates/api/front/dashboard/data/sessions/'+session.id+'/mode/resit')
                    .then(resp => {
                        this.reload()
                    })
                    .catch(err => {
                        this.$auth.handleErrorVm(err, this)
                    })        
            },

            delete(session) {
                this.ctrl.modals.open('confirmDelete').then(confirmResponse => {
                    if (confirmResponse.confirmed) {
                        axios.post('/socrates/api/front/dashboard/data/sessions/'+session.id+'/delete')
                            .then(resp => {
                                this.reload()
                            })
                            .catch(err => {
                                this.$auth.handleErrorVm(err, this)
                            })        
                        }
                })
            },

            sortChange(data) {
                this.filtering.sortChange(data, this.sortDefault)
            },

            tableFiltersChange(data) {
                this.filtering.filterChange(data)
            },

            globalFiltersChange(data) {
                this.filters = { ...this.filters, ...data}
                this.reload()
            },

            filterByLearner(learner) {
                this.filters.learner_id = learner.id
                this.filters = {...this.filters}    // Clone to force reactivity
                this.reload()
            },

            filterByDegree(degree) {
                this.filters.degree_id = degree.id
                this.filters = {...this.filters}    // Clone to force reactivity
                this.reload()
            },

            filterByCohort(cohort) {
                this.filters.cohort_id = cohort.id
                this.filters = {...this.filters}    // Clone to force reactivity
                this.reload()
            },

            filterByTester(tester) {
                this.filters.tester_id = tester.id
                this.filters = {...this.filters}    // Clone to force reactivity
                this.reload()
            },

            clearFilters() {
                if (this.registrationId && this.learnerId) {
                    this.filtering.clearFilters({registration_id: this.registrationId, learner_id: this.learnerId}, this.sortDefault)
                } else if (this.registrationId) {
                    this.filtering.clearFilters({registration_id: this.registrationId}, this.sortDefault)
                } else {
                    this.filtering.clearFilters({}, this.sortDefault)
                }
            },

            listed() {
                this.filtering.saveUiConfig()
            },

            getFilters() {
                let filters = {$and: []}
                
                // Registration.
                if (this.registrationId) {
                    filters.$and.push(
                        {registration_id: this.registrationId}
                    )
                }

                // Learner.
                if (this.learnerId) {
                    filters.$and.push(
                        {learner_id: this.learnerId}
                    )
                }

                // Status.
                if (this.filters.status) {
                    filters.$and.push(
                        {status: {$in: this.filters.status }}
                    )
                }
                
                // Mode.
                if (this.filters.mode) {
                    filters.$and.push(
                        {mode: {$in: this.filters.mode }}
                    )
                }
                
                // Result.
                if (this.filters.gonogo) {
                    filters.$and.push(
                        {gonogo: {$in: this.filters.gonogo }}
                    )
                }

                // Search.
                if (this.filters.simpleSearch) {
                    filters.$and.push({ simpleSearch: this.filters.simpleSearch})
                }
                
                // Addtional filters.
                if (this.filters.cohort_id) {
                    filters.$and.push(
                        {cohort_id: this.filters.cohort_id}
                    )
                }
                if (this.filters.degree_id) {
                    filters.$and.push(
                        {degree_id: this.filters.degree_id}
                    )
                }
                if (!this.learnerId && this.filters.learner_id) {
                    filters.$and.push(
                        {learner_id: this.filters.learner_id}
                    )
                }
                if (this.filters.tester_id) {
                    filters.$and.push(
                        {testerId: this.filters.tester_id}
                    )
                }

                if (filters.$and.length == 0) {
                    filters = {}
                }
                return filters
            },

            reload() {
                this.ctrl.filter(
                    this.getFilters()
                )
            },
           
            statusColor(status) {
                return this.statusColors[status]
            },

            statusLabel(status) {
                return this.statusData.find(item => item.id == status).name
            },
           
            gonogoColor(gonogo) {
                return this.gonogoColors[gonogo]
            },

            gonogoLabel(gonogo) {
                return this.gonogoSelect[gonogo]
            },

            resultAvailable(session) {
                return session.gonogo && (
                    session.status == 'completed' || session.status == 'closed' || session.status == 'archived'
                )
            },

            scheduledDate(session) {
                return this.moment(session.scheduled_at).format('YYYY/MM/DD HH:mm')
            },

            openFilters() {
                this.ctrl.modals.open('filters').then(filters => {
                    this.reload()
                })
            },
        }
    }
</script>

<style scoped>

    .card >>> th,
    .card >>> td {
        padding-right: 0;
    }
    .card >>> th.avatars,
    .card >>> td.avatars {
        padding-left: 0.5rem;
    }
    .card >>> th.learners,
    .card >>> td.learners {
        padding-left: 1rem;
    }
    .card >>> th.actions,
    .card >>> td.actions {
        padding-left: 0rem;
        padding-right: 1rem;
    }
    .card >>> td.result .badge {
        color: white;
        font-size: .6rem;
    }
    .card >>> td.result .badge-success {
        background-color: #1aae6f;
    }
    .card >>> td.result .badge-danger {
        background-color: #f80031;
    }
    .status-bullet {
        font-size: 8px;
    }

</style>
